import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Container,
  Stack,
  Text,
  Loader,
  Accordion,
  TextInput,
  Textarea,
  Button,
  Group,
  ActionIcon,
  Paper,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { AdminNavbar } from '@/components/AdminNavbar/AdminNavbar'
import { ResponsiveTitle } from '@/components/ResponsiveTitle/ResponsiveTitle'
import { isAdminAuthenticated, refreshAdminSession } from '@/lib/auth'
import { useSiteContent } from '@/hooks/useSiteContent'
import { useUpdateContent } from '@/hooks/useUpdateContent'
import type { SiteContent } from '@/lib/types'
import styles from './content.module.css'

export const Route = createFileRoute('/admin/content')({
  component: AdminContentEditor,
})

/**
 * Admin page for editing all guest-facing site content.
 * Provides an accordion-based form interface to edit:
 * - Event details (couple names, invitation text, title, date)
 * - Location information
 * - Contact information
 * - Invitation story text
 * - Gift text
 * - Timeline entries
 *
 * Requires admin authentication. Redirects to login if not authenticated.
 */
function AdminContentEditor() {
  const navigate = useNavigate()
  const { content, loading: contentLoading, refetch } = useSiteContent()
  const { updateContent, saving, error: updateError } = useUpdateContent()

  // Form state
  const [formData, setFormData] = useState<SiteContent | null>(null)

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate({ to: '/admin/login' })
      return
    }

    refreshAdminSession()

    const interval = setInterval(() => {
      if (!isAdminAuthenticated()) {
        navigate({ to: '/admin/login' })
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [navigate])

  // Initialize form data when content loads
  useEffect(() => {
    if (content && !formData) {
      setFormData(content)
    }
  }, [content, formData])

  const handleFieldChange = (path: string[], value: string) => {
    if (!formData) return

    const newData = { ...formData }
    let current: any = newData

    // Navigate to the nested property
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i]
      if (key === undefined) continue
      current = current[key]
    }

    // Set the value
    const lastKey = path[path.length - 1]
    if (lastKey !== undefined) {
      current[lastKey] = value
    }

    setFormData(newData)
  }

  const handleTimelineChange = (index: number, field: 'time' | 'description', value: string) => {
    if (!formData) return

    const newTimeline = [...formData.timeline]
    const existingEntry = newTimeline[index]
    if (existingEntry) {
      newTimeline[index] = { ...existingEntry, [field]: value }
    }

    setFormData({
      ...formData,
      timeline: newTimeline,
    })
  }

  const handleAddTimelineEntry = () => {
    if (!formData) return

    setFormData({
      ...formData,
      timeline: [...formData.timeline, { time: '', description: '' }],
    })
  }

  const handleRemoveTimelineEntry = (index: number) => {
    if (!formData) return

    const newTimeline = formData.timeline.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      timeline: newTimeline,
    })
  }

  const handleSave = async () => {
    if (!formData) return

    const success = await updateContent(formData)

    if (success) {
      notifications.show({
        title: 'Erfolgreich gespeichert',
        message: 'Die Inhalte wurden erfolgreich aktualisiert.',
        color: 'green',
      })
      // Refetch to get latest data
      await refetch()
    } else {
      notifications.show({
        title: 'Fehler beim Speichern',
        message: updateError || 'Die Inhalte konnten nicht gespeichert werden.',
        color: 'red',
        autoClose: 10000,
      })
    }
  }

  if (contentLoading || !formData) {
    return (
      <div className={styles.adminLayout}>
        <AdminNavbar />
        <Container size="xl" py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Lädt Inhalte...</Text>
          </Stack>
        </Container>
      </div>
    )
  }

  return (
    <div className={styles.adminLayout}>
      <AdminNavbar />

      <Container size="xl" py="xl">
        <Stack gap="xl">
          <ResponsiveTitle order={1}>Inhalte bearbeiten</ResponsiveTitle>
          <Text size="lg" c="dimmed">
            Bearbeiten Sie alle Gäste-Texte und Veranstaltungsdetails.
          </Text>

          <Paper p="xl" shadow="sm" radius="md" withBorder>
            <Accordion defaultValue="event" variant="separated">
              {/* Veranstaltungsdetails */}
              <Accordion.Item value="event">
                <Accordion.Control>
                  <Text fw={600}>Veranstaltungsdetails</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <TextInput
                      label="Paarnamen"
                      value={formData.event.coupleNames}
                      onChange={(e) => handleFieldChange(['event', 'coupleNames'], e.target.value)}
                      required
                    />
                    <TextInput
                      label="Einladungstext (z.B. 'laden herzlich ein zur')"
                      value={formData.event.invitationText}
                      onChange={(e) =>
                        handleFieldChange(['event', 'invitationText'], e.target.value)
                      }
                      placeholder="laden herzlich ein zur"
                      required
                    />
                    <TextInput
                      label="Event-Titel"
                      value={formData.event.eventTitle}
                      onChange={(e) => handleFieldChange(['event', 'eventTitle'], e.target.value)}
                      required
                    />
                    <TextInput
                      label="Datum"
                      value={formData.event.eventDate}
                      onChange={(e) => handleFieldChange(['event', 'eventDate'], e.target.value)}
                      placeholder="19.09.2026"
                      required
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Ort */}
              <Accordion.Item value="location">
                <Accordion.Control>
                  <Text fw={600}>Ort</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <TextInput
                      label="Name"
                      value={formData.location.name}
                      onChange={(e) => handleFieldChange(['location', 'name'], e.target.value)}
                      required
                    />
                    <TextInput
                      label="Adresse Zeile 1"
                      value={formData.location.addressLine1}
                      onChange={(e) =>
                        handleFieldChange(['location', 'addressLine1'], e.target.value)
                      }
                      required
                    />
                    <TextInput
                      label="Adresse Zeile 2"
                      value={formData.location.addressLine2}
                      onChange={(e) =>
                        handleFieldChange(['location', 'addressLine2'], e.target.value)
                      }
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Kontakt */}
              <Accordion.Item value="contact">
                <Accordion.Control>
                  <Text fw={600}>Kontakt</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <TextInput
                      label="Telefon 1 Label"
                      value={formData.contact.primaryPhoneLabel}
                      onChange={(e) =>
                        handleFieldChange(['contact', 'primaryPhoneLabel'], e.target.value)
                      }
                    />
                    <TextInput
                      label="Telefon 1 Nummer"
                      value={formData.contact.primaryPhoneNumber}
                      onChange={(e) =>
                        handleFieldChange(['contact', 'primaryPhoneNumber'], e.target.value)
                      }
                      placeholder="+49 162 2685577"
                      required
                    />
                    <TextInput
                      label="Telefon 2 Label"
                      value={formData.contact.secondaryPhoneLabel}
                      onChange={(e) =>
                        handleFieldChange(['contact', 'secondaryPhoneLabel'], e.target.value)
                      }
                    />
                    <TextInput
                      label="Telefon 2 Nummer"
                      value={formData.contact.secondaryPhoneNumber}
                      onChange={(e) =>
                        handleFieldChange(['contact', 'secondaryPhoneNumber'], e.target.value)
                      }
                      placeholder="+49 176 67775569"
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Einladungstext */}
              <Accordion.Item value="invitation">
                <Accordion.Control>
                  <Text fw={600}>Einladungstext</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <Textarea
                      label="Geschichte/Text"
                      value={formData.invitation.storyText}
                      onChange={(e) =>
                        handleFieldChange(['invitation', 'storyText'], e.target.value)
                      }
                      autosize
                      minRows={4}
                      required
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Geschenktext */}
              <Accordion.Item value="gift">
                <Accordion.Control>
                  <Text fw={600}>Geschenktext</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <Textarea
                      label="Geschenktext"
                      value={formData.invitation.giftText}
                      onChange={(e) =>
                        handleFieldChange(['invitation', 'giftText'], e.target.value)
                      }
                      autosize
                      minRows={4}
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Zeitplan */}
              <Accordion.Item value="timeline">
                <Accordion.Control>
                  <Text fw={600}>Zeitplan</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    {formData.timeline.map((entry, index) => (
                      <Group key={index} align="flex-start" gap="sm">
                        <TextInput
                          label="Uhrzeit"
                          value={entry.time}
                          onChange={(e) => handleTimelineChange(index, 'time', e.target.value)}
                          placeholder="14:00 Uhr"
                          style={{ flex: '0 0 150px' }}
                          required
                        />
                        <TextInput
                          label="Beschreibung"
                          value={entry.description}
                          onChange={(e) =>
                            handleTimelineChange(index, 'description', e.target.value)
                          }
                          placeholder="Freie Trauung unter der Walnuss"
                          style={{ flex: 1 }}
                          required
                        />
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => handleRemoveTimelineEntry(index)}
                          mt="1.5rem"
                          aria-label="Eintrag entfernen"
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Group>
                    ))}
                    <Button
                      leftSection={<IconPlus size={18} />}
                      variant="light"
                      onClick={handleAddTimelineEntry}
                      mt="sm"
                    >
                      Eintrag hinzufügen
                    </Button>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* RSVP Seite */}
              <Accordion.Item value="rsvp">
                <Accordion.Control>
                  <Text fw={600}>RSVP Seite</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <Textarea
                      label="Beschreibung (mehrere Gäste)"
                      value={formData.rsvp.descriptionMultiple}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'descriptionMultiple'], e.target.value)
                      }
                      placeholder="Teilt uns bitte mit, ob Ihr an unserer Hochzeit teilnehmen mögt."
                      autosize
                      minRows={2}
                    />
                    <Textarea
                      label="Beschreibung (einzelner Gast)"
                      value={formData.rsvp.descriptionSingle}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'descriptionSingle'], e.target.value)
                      }
                      placeholder="Bitte geben Sie uns Bescheid, ob Sie an unserer Hochzeit teilnehmen können."
                      autosize
                      minRows={2}
                    />
                    <Text fw={600} mt="md">
                      Ernährungswünsche
                    </Text>
                    <TextInput
                      label="Titel"
                      value={formData.rsvp.dietaryTitle}
                      onChange={(e) => handleFieldChange(['rsvp', 'dietaryTitle'], e.target.value)}
                      placeholder="Besondere Ernährungswünsche"
                    />
                    <TextInput
                      label="Vegan Label"
                      value={formData.rsvp.dietaryVegan}
                      onChange={(e) => handleFieldChange(['rsvp', 'dietaryVegan'], e.target.value)}
                      placeholder="Vegan"
                    />
                    <TextInput
                      label="Glutenfrei Label"
                      value={formData.rsvp.dietaryGlutenFree}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'dietaryGlutenFree'], e.target.value)
                      }
                      placeholder="Glutenfrei"
                    />
                    <TextInput
                      label="Laktosefrei Label"
                      value={formData.rsvp.dietaryLactoseFree}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'dietaryLactoseFree'], e.target.value)
                      }
                      placeholder="Laktosefrei"
                    />
                    <TextInput
                      label="Allergien Label"
                      value={formData.rsvp.dietaryAllergiesLabel}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'dietaryAllergiesLabel'], e.target.value)
                      }
                      placeholder="Allergien"
                    />
                    <TextInput
                      label="Allergien Placeholder"
                      value={formData.rsvp.dietaryAllergiesPlaceholder}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'dietaryAllergiesPlaceholder'], e.target.value)
                      }
                      placeholder="z.B. Nüsse, Meeresfrüchte"
                    />
                    <TextInput
                      label="Sonstiges Label"
                      value={formData.rsvp.dietaryMiscLabel}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'dietaryMiscLabel'], e.target.value)
                      }
                      placeholder="Sonstiges"
                    />
                    <TextInput
                      label="Sonstiges Placeholder"
                      value={formData.rsvp.dietaryMiscPlaceholder}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'dietaryMiscPlaceholder'], e.target.value)
                      }
                      placeholder="Weitere Ernährungswünsche"
                    />
                    <Text fw={600} mt="md">
                      Zusätzliche Gäste
                    </Text>
                    <TextInput
                      label="Titel"
                      value={formData.rsvp.additionalGuestsTitle}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'additionalGuestsTitle'], e.target.value)
                      }
                      placeholder="Zusätzliche Gäste"
                    />
                    <TextInput
                      label="Keine Gäste Text"
                      value={formData.rsvp.noAdditionalGuests}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'noAdditionalGuests'], e.target.value)
                      }
                      placeholder="Noch keine zusätzlichen Gäste hinzugefügt"
                    />
                    <TextInput
                      label="+1 hinzufügen Button"
                      value={formData.rsvp.addPlusOne}
                      onChange={(e) => handleFieldChange(['rsvp', 'addPlusOne'], e.target.value)}
                      placeholder="+1 hinzufügen"
                    />
                    <TextInput
                      label="Kind hinzufügen Button"
                      value={formData.rsvp.addChild}
                      onChange={(e) => handleFieldChange(['rsvp', 'addChild'], e.target.value)}
                      placeholder="Kind hinzufügen"
                    />
                    <Text fw={600} mt="md">
                      Kommentare
                    </Text>
                    <TextInput
                      label="Kommentare Label"
                      value={formData.rsvp.commentsLabel}
                      onChange={(e) => handleFieldChange(['rsvp', 'commentsLabel'], e.target.value)}
                      placeholder="Kommentare"
                    />
                    <TextInput
                      label="Kommentare Placeholder"
                      value={formData.rsvp.commentsPlaceholder}
                      onChange={(e) =>
                        handleFieldChange(['rsvp', 'commentsPlaceholder'], e.target.value)
                      }
                      placeholder="Weitere Anmerkungen oder Fragen"
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Fotos Seite */}
              <Accordion.Item value="photos">
                <Accordion.Control>
                  <Text fw={600}>Fotos Seite</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md" mt="md">
                    <Textarea
                      label="Beschreibung"
                      value={formData.photos.description}
                      onChange={(e) => handleFieldChange(['photos', 'description'], e.target.value)}
                      placeholder="Teilt jene Augenblicke mit uns, die diese festliche Hochzeit besonders wertvoll machen..."
                      autosize
                      minRows={3}
                    />
                    <Text fw={600} mt="md">
                      Löschen Modal
                    </Text>
                    <TextInput
                      label="Modal Titel"
                      value={formData.photos.deleteModalTitle}
                      onChange={(e) =>
                        handleFieldChange(['photos', 'deleteModalTitle'], e.target.value)
                      }
                      placeholder="Foto löschen"
                    />
                    <Textarea
                      label="Modal Text"
                      value={formData.photos.deleteModalText}
                      onChange={(e) =>
                        handleFieldChange(['photos', 'deleteModalText'], e.target.value)
                      }
                      placeholder="Möchten Sie dieses Foto wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
                      autosize
                      minRows={2}
                    />
                    <TextInput
                      label="Löschen Button"
                      value={formData.photos.deleteButton}
                      onChange={(e) =>
                        handleFieldChange(['photos', 'deleteButton'], e.target.value)
                      }
                      placeholder="Löschen"
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Group justify="flex-end" mt="xl">
              <Button onClick={handleSave} loading={saving} size="lg">
                Speichern
              </Button>
            </Group>
          </Paper>
        </Stack>
      </Container>
    </div>
  )
}
